// src/components/achievement/CommunityLinks.tsx
import React from 'react';
import { LinkIcon, VideoCameraIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { Link } from 'react-router-dom';

export type AchievementLink = {
  id: number;
  url: string;
  title: string;
  type: 'youtube' | 'website';
};

type Props = {
  links: AchievementLink[];
  userIsAuthenticated: boolean;
  isAddingLink: boolean;
  linkError: string | null;
  newLink: { url: string; title: string; type: 'youtube' | 'website' };
  onChangeNewLink: (field: 'url' | 'title' | 'type', value: string) => void;
  onAddLink: () => void;
  onStartAdding: () => void;
  onCancelAdd: () => void;
  onDelete: (linkId: number) => void;
  onOpenYoutubeModal: (link: AchievementLink) => void;
};

const getYoutubeVideoId = (url: string): string | null => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const CommunityLinks: React.FC<Props> = ({
  links,
  userIsAuthenticated,
  isAddingLink,
  linkError,
  newLink,
  onChangeNewLink,
  onAddLink,
  onStartAdding,
  onCancelAdd,
  onDelete,
  onOpenYoutubeModal
}) => {
  const { t } = useLanguage();
  const hasYouTube = links.some(link => link.type === 'youtube');
  const hasWebsites = links.some(link => link.type === 'website');

  return (
    <div className="bg-gray-800 rounded-lg p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-white">{t('achievements.communityLinks')}</h2>
        {userIsAuthenticated && !isAddingLink ? (
          <button
            onClick={onStartAdding}
            className="bg-destiny-blue hover:bg-opacity-90 text-white text-sm px-3 py-1 rounded flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            {t('achievements.addLink')}
          </button>
        ) : !userIsAuthenticated && (
          <div className="text-sm text-gray-400">
            <Link to="/login" className="text-destiny-blue hover:text-destiny-gold">{t('nav.login')}</Link> {t('achievements.signInToAdd')}
          </div>
        )}
      </div>

      {isAddingLink && (
        <div className="bg-gray-700 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-white font-medium">{t('achievements.addLink')}</h3>
            <button onClick={onCancelAdd} className="text-gray-400 hover:text-white">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {linkError && (
            <div className="bg-destiny-red bg-opacity-20 border border-destiny-red rounded p-2 mb-4 text-sm text-destiny-red">
              {linkError}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('achievements.linkTitle')}</label>
              <input
                type="text"
                value={newLink.title}
                onChange={(e) => onChangeNewLink('title', e.target.value)}
                placeholder={t('achievements.linkTitlePlaceholder')}
                className="w-full bg-gray-600 border-gray-500 rounded p-2 text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('achievements.linkUrl')}</label>
              <input
                type="url"
                value={newLink.url}
                onChange={(e) => onChangeNewLink('url', e.target.value)}
                placeholder={t('achievements.linkUrlPlaceholder')}
                className="w-full bg-gray-600 border-gray-500 rounded p-2 text-white"
              />
              <p className="text-xs text-gray-400 mt-1">{t('achievements.youtubeAutodetect')}</p>
            </div>

            <button
              onClick={onAddLink}
              className="bg-destiny-blue hover:bg-opacity-90 text-white px-4 py-2 rounded w-full"
            >
              {t('achievements.addLink')}
            </button>
          </div>
        </div>
      )}

      {hasYouTube && (
        <div className="mb-6">
          <h3 className="text-destiny-gold font-medium mb-3 flex items-center">
            <VideoCameraIcon className="h-5 w-5 mr-2" />
            {t('achievements.videoGuides')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {links.filter(l => l.type === 'youtube').map(link => {
              const videoId = getYoutubeVideoId(link.url);
              const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
              return (
                <div key={link.id} className="bg-gray-700 rounded-lg overflow-hidden hover:bg-gray-600 transition-colors">
                  <div className="aspect-w-16 aspect-h-9 cursor-pointer" onClick={() => onOpenYoutubeModal(link)}>
                    {thumbnailUrl ? (
                      <div className="relative w-full h-full">
                        <img src={thumbnailUrl} alt={link.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-destiny-dark bg-opacity-70 rounded-full p-3">
                            <svg className="w-8 h-8 text-destiny-gold" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                        <VideoCameraIcon className="h-12 w-12 text-gray-600" />
                      </div>
                    )}
                  </div>
                  <div className="p-3 flex justify-between items-center">
                    <h4 className="text-white font-medium truncate" title={link.title}>{link.title}</h4>
                    {userIsAuthenticated && (
                      <button
                        onClick={() => onDelete(link.id)}
                        className="text-gray-400 hover:text-destiny-red ml-2 flex-shrink-0"
                        title={t('achievements.deleteLink')}
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {hasWebsites && (
        <div>
          <h3 className="text-destiny-gold font-medium mb-3 flex items-center">
            <LinkIcon className="h-5 w-5 mr-2" />
            {t('achievements.helpfulWebsites')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {links.filter(l => l.type === 'website').map(link => (
              <div key={link.id} className="bg-gray-700 rounded-lg p-3 flex justify-between items-center">
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-destiny-blue hover:text-destiny-gold truncate flex-1"
                >
                  {link.title}
                </a>
                {userIsAuthenticated && (
                  <button
                    onClick={() => onDelete(link.id)}
                    className="text-gray-400 hover:text-destiny-red ml-2"
                    title={t('achievements.deleteLink')}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {links.length === 0 && (
        <div className="bg-gray-700 rounded p-4 text-center text-gray-300">
          {t('achievements.noLinks')} {userIsAuthenticated ? t('achievements.beFirstToAdd') : t('achievements.signInToAdd')}
        </div>
      )}
    </div>
  );
};

export default CommunityLinks;